import { useCallback, useEffect, useMemo, useState } from 'react'

import { useConnect, useReduxBundlerStore } from 'redux-bundler-hook'

import { Close } from '@mui/icons-material'
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
} from '@mui/material'

import { AttachmentList } from '@common/components'
import {
  getApiFetch,
  openInNewTab,
  parseApiErrors,
  useSmallScreen,
} from '@common/utils'
import ConfirmationDialog from '@portal/UI/components/ConfirmationDialog'

/**
 * @component
 * @param {Object} props - The props for the component.
 * @param {boolean} props.open
 * @param {Function} props.onClose
 * @param {Object[]} props.attachments
 */
export default function AttachmentsModal({ open, onClose, attachments }) {
  const isSmallScreen = useSmallScreen()

  const { isAtLeastAdmin, doShowSnackbar } = useConnect(
    'selectIsAtLeastAdmin',
    'doShowSnackbar',
  )

  const store = useReduxBundlerStore()
  const apiFetch = getApiFetch(store)

  const [loading, setLoading] = useState(false)
  const [attachmentsToRemove, setAttachmentsToRemove] = useState([])
  const [openConfirmation, setOpenConfirmation] = useState(false)

  // eslint-disable-next-line arrow-body-style
  useEffect(() => {
    return () => {
      setAttachmentsToRemove([])
    }
  }, [open])

  const visibleAttachments = useMemo(
    () =>
      attachments?.filter(
        (attachment) => !attachmentsToRemove.find((item) => item.id === attachment.id),
      ) ?? [],
    [attachments, attachmentsToRemove],
  )

  const handleSave = useCallback(async () => {
    try {
      setLoading(true)
      await apiFetch(
        '/smoke_event_feedback_attachments/',
        attachmentsToRemove.map((item) => ({ id: item.id })),
        {
          cancelationPrefix: 'feedback_adjustments_modal',
          method: 'DELETE',
        },
      )
      onClose(true)
    } catch (err) {
      const parsedError = parseApiErrors(err?.response)
      doShowSnackbar(parsedError, 'error')
    } finally {
      setLoading(false)
    }
  }, [attachmentsToRemove])

  return (
    <>
      <ConfirmationDialog
        fullWidth={false}
        title="Delete attachments"
        content={
          <Box display="flex" flexDirection="column" mb={2}>
            {attachmentsToRemove.map((attachment) => (
              <Typography key={attachment.id} variant="caption" color="error.main">
                {attachment.filename}
              </Typography>
            ))}
          </Box>
        }
        message="These attachments will be deleted. Are you sure you want to proceed?"
        open={openConfirmation}
        onConfirm={() => {
          setOpenConfirmation(false)
          handleSave()
        }}
        onCancel={() => setOpenConfirmation(false)}
      />
      <Dialog fullWidth maxWidth="md" open={open} onClose={onClose}>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <DialogTitle>
            Feedback Attachments ({visibleAttachments?.length ?? 0})
          </DialogTitle>
          <IconButton disabled={loading} onClick={onClose} sx={{ mr: 1.5 }}>
            <Close />
          </IconButton>
        </Box>
        <DialogContent>
          {visibleAttachments.length ? (
            <AttachmentList
              type="remote"
              orientation={isSmallScreen.mobile ? 'vertical' : 'horizontal'}
              attachments={visibleAttachments}
              onRemove={
                loading || !isAtLeastAdmin
                  ? null
                  : (item) => setAttachmentsToRemove((prev) => [...prev, item])
              }
              onClick={loading ? null : (file) => openInNewTab(file.url)}
            />
          ) : (
            <Typography variant="h6" color="grey" textAlign="center" my={9.6}>
              No Attachments Found
            </Typography>
          )}
        </DialogContent>
        {isAtLeastAdmin && (
          <DialogActions sx={{ mx: 1, mb: 1, gap: 0.5 }}>
            <Button disabled={loading} variant="outlined" onClick={onClose}>
              Cancel
            </Button>
            <Button
              disabled={!attachmentsToRemove.length}
              variant="outlined"
              onClick={() => setAttachmentsToRemove([])}
            >
              Revert
            </Button>
            <Button
              variant="contained"
              loading={loading}
              disabled={!attachmentsToRemove.length}
              onClick={() => setOpenConfirmation(true)}
            >
              Save
            </Button>
          </DialogActions>
        )}
      </Dialog>
    </>
  )
}
